import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useAvailableICcIdsByState } from '../useICcIds';
import { ICcIdResponse } from '../../types/dtos';
import {
  FASTCHIP_CLUSTER_CP,
  FASTCHIP_CLUSTER_HB,
  formatRjMgEsFastchipClusterUiLabel,
  getCpValidDdds,
  getDhValidDddsForFastchipLines,
  getEr2ValidDddsForFastchipLines,
  getHbCpUnionValidDdds,
  getHbValidDdds,
  getBaValidDdds,
  getHdm2ValidDdds,
  iccidHasCluster,
  iccidHasDhCluster,
  isHbCpMarketEstado,
  isHdm2MarketEstado,
  resolveRjMgEsFastchipIccidSearch,
  FASTCHIP_CLUSTER_HDM2,
} from '../../utils/fastchipClusterRules';
import { isDhContractToken } from '../../utils/contractClusterTokens';

/**
 * Hook com a lógica de DDD/ICCID e opções de DDD para o formulário de contrato.
 * Fastchip RJ/MG/ES: E.R.2 quando `DH_FASTCHIP_ENABLED=false`; DH quando true
 * (DDDs de linha, só MG). Por entrega: DDDs da UF do cliente, mesmo com token DH.
 */
export interface UseContractFormDddIccidOptions {
  clientEstado?: string;
  fastchip: boolean;
  setFastchip?: (value: boolean) => void;
  /** Flag para indicar se está editando um contrato existente (preserva fastchip e ICCIDs) */
  isEditingExistingContract?: boolean;
  /**
   * Linhas já adicionadas ao contrato (usadas para inferir o cluster PR fixo).
   * Quando o clientEstado é PR e existe uma linha com FAST atribuído,
   * o hook filtra os ICCIDs disponíveis para o mesmo cluster.
   */
  linhasComFast?: { chipCcid?: string }[];
  /**
   * Token de cluster do formulário (`DH`, `DH - RJ/MG/ES`, `E.R.2 GESTAO`, …).
   * Fastchip + token DH: pool de linha (MG 31–38). Por entrega: DDDs da UF do cliente.
   */
  saleToken?: string;
  /** Quando true, não carrega pool de ICCIDs no frontend (alocação fica 100% no backend). */
  backendAllocationEnabled?: boolean;
}

const STATE_DDD_MAP: { [key: string]: string[] } = {
  AC: ['68'],
  AL: ['82'],
  AP: ['96'],
  AM: ['92', '97'],
  BA: ['71', '73', '74', '75', '77'],
  CE: ['85', '88'],
  DF: ['61'],
  ES: ['27', '28'],
  GO: ['62', '64'],
  MA: ['98', '99'],
  MT: ['65', '66'],
  MS: ['67'],
  MG: ['31', '32', '33', '34', '35', '37', '38'],
  PA: ['91', '93', '94'],
  PB: ['83'],
  PR: ['41', '42', '43', '44', '45', '46'],
  PE: ['81', '87'],
  PI: ['86', '89'],
  RJ: ['21', '22', '24'],
  RN: ['84'],
  RS: ['51', '52', '53', '54', '55'],
  RO: ['69'],
  RR: ['95'],
  SC: ['47', '48', '49'],
  SP: ['11', '12', '13', '14', '15', '16', '17', '18', '19'],
  SE: ['79'],
  TO: ['63'],
};

export function getValidDdds(
  estado: string,
  isFastchip: boolean,
  cluster?: string,
  saleToken?: string
): string[] {
  // Fastchip + token DH: pool de linha (MG 31–38), inclusive cliente RJ/ES.
  // Por entrega ignora o token e usa STATE_DDD_MAP da UF.
  if (isFastchip && isDhContractToken(saleToken)) {
    return getDhValidDddsForFastchipLines();
  }

  if (!estado) return [];
  const estadoUpper = estado.toUpperCase();
  const dddNormal = STATE_DDD_MAP[estadoUpper] || [];

  if (isFastchip) {
    switch (estadoUpper) {
      case 'SC':
        return ['47', '48', '49'];
      case 'SP':
        return ['11', '12', '13', '14', '15', '16', '17', '18', '19'];
      case 'PR':
        // MH (cidades Guarapuava): só DDD 42. ER / sem cluster: 41–45 — 42 permitido fora do MH → E.R.GESTAO
        if (cluster === 'MH') return ['42'];
        return ['41', '42', '43', '44', '45'];
      case 'RJ':
      case 'MG':
      case 'ES':
        return getDhValidDddsForFastchipLines();
      case 'PE':
      case 'CE':
      case 'PI':
      case 'AL':
      case 'RN':
      case 'PB':
        return getHbCpUnionValidDdds();
      case 'BA':
        return getBaValidDdds();
      case 'RS':
        return getHdm2ValidDdds();
      default:
        return dddNormal;
    }
  }
  return dddNormal;
}

export function useContractFormDddIccid(options: UseContractFormDddIccidOptions) {
  const {
    clientEstado = '',
    fastchip,
    setFastchip,
    isEditingExistingContract = false,
    linhasComFast,
    saleToken = '',
    backendAllocationEnabled = false,
  } = options;

  const { user } = useAuth();
  const teamClusterPreference = (user?.rjMgEsFastchipClusterPreference as 'ER2' | 'DH' | null | undefined) ?? null;

  const {
    iccids: availableICcIdsRaw,
    loading: iccidsLoading,
    error: iccidsError,
    hasMore: hasMoreIccids,
    loadMore: loadMoreIccids,
    refetch: refetchIccids,
  } = useAvailableICcIdsByState(clientEstado, {
    compact: true,
    autoLoad: !backendAllocationEnabled,
  });

  const applyClusterFilters = useCallback(
    (source: ICcIdResponse[]): ICcIdResponse[] => {
      let filtered = source.filter((ic: ICcIdResponse) => ic.disponivel === true);

      const rjMgEsSearch = resolveRjMgEsFastchipIccidSearch(clientEstado, teamClusterPreference);
      if (rjMgEsSearch?.usesDhCluster) {
        filtered = filtered.filter((ic: ICcIdResponse) => iccidHasDhCluster(ic.estadosInfo));
      }

      if (clientEstado === 'PR' && linhasComFast && linhasComFast.length > 0) {
        const firstCcid = linhasComFast.find((l) => l.chipCcid)?.chipCcid;
        if (firstCcid) {
          const firstIccid = availableICcIdsRaw.find((ic: ICcIdResponse) => ic.ccId === firstCcid);
          const prEstadoInfo = firstIccid?.estadosInfo?.find(
            (e: { estado: string; cluster?: string | null }) => e.estado === 'PR'
          );
          const fixedCluster = prEstadoInfo?.cluster;
          if (fixedCluster) {
            filtered = filtered.filter((ic: ICcIdResponse) => {
              const prInfo = ic.estadosInfo?.find(
                (e: { estado: string; cluster?: string | null }) => e.estado === 'PR'
              );
              if (fixedCluster === 'ER') {
                return !prInfo?.cluster || prInfo.cluster === 'ER';
              }
              return prInfo?.cluster === fixedCluster;
            });
          }
        }
      }

      if (isHbCpMarketEstado(clientEstado) && linhasComFast && linhasComFast.length > 0) {
        const firstCcid = linhasComFast.find((l) => l.chipCcid)?.chipCcid;
        if (firstCcid) {
          const firstIccid = availableICcIdsRaw.find((ic: ICcIdResponse) => ic.ccId === firstCcid);
          let fixedCluster: string | null | undefined;
          if (iccidHasCluster(firstIccid?.estadosInfo, FASTCHIP_CLUSTER_HB)) {
            fixedCluster = FASTCHIP_CLUSTER_HB;
          } else if (iccidHasCluster(firstIccid?.estadosInfo, FASTCHIP_CLUSTER_CP)) {
            fixedCluster = FASTCHIP_CLUSTER_CP;
          }
          if (fixedCluster) {
            filtered = filtered.filter((ic: ICcIdResponse) =>
              iccidHasCluster(ic.estadosInfo, fixedCluster!)
            );
          }
        }
      }

      if (isHdm2MarketEstado(clientEstado) && linhasComFast && linhasComFast.length > 0) {
        const firstCcid = linhasComFast.find((l) => l.chipCcid)?.chipCcid;
        if (firstCcid) {
          const firstIccid = availableICcIdsRaw.find((ic: ICcIdResponse) => ic.ccId === firstCcid);
          if (iccidHasCluster(firstIccid?.estadosInfo, FASTCHIP_CLUSTER_HDM2)) {
            filtered = filtered.filter((ic: ICcIdResponse) =>
              iccidHasCluster(ic.estadosInfo, FASTCHIP_CLUSTER_HDM2)
            );
          }
        }
      }

      return filtered;
    },
    [availableICcIdsRaw, clientEstado, linhasComFast, teamClusterPreference]
  );

  const availableICcIdsBase = useMemo(() => {
    if (backendAllocationEnabled) return [];
    return applyClusterFilters(availableICcIdsRaw);
  }, [backendAllocationEnabled, applyClusterFilters, availableICcIdsRaw]);

  const availableICcIds = availableICcIdsBase;

  const getEstadosFromAvailableICcIds = useCallback((): string[] => {
    if (availableICcIds.length === 0) return [];
    const estadosSet = new Set<string>();
    availableICcIds.forEach((iccid: ICcIdResponse) => {
      if (iccid.estados && iccid.estados.length > 0) {
        iccid.estados.forEach((estado: string) => estadosSet.add(estado));
      }
      if (iccid.estado) estadosSet.add(iccid.estado);
    });
    return Array.from(estadosSet);
  }, [availableICcIds]);

  const getValidDddsWithICcIdFallback = useCallback(
    (estado: string, isFastchip: boolean, cluster?: string): string[] => {
      if (!isFastchip) return getValidDdds(estado, isFastchip, cluster, saleToken);
      if (backendAllocationEnabled) return getValidDdds(estado, isFastchip, cluster, saleToken);
      if (availableICcIds.length === 0) return [];
      const estadoUpper = estado?.toUpperCase() || '';
      const rjMgEsSearch = resolveRjMgEsFastchipIccidSearch(estadoUpper, teamClusterPreference);
      if (rjMgEsSearch || isDhContractToken(saleToken)) {
        if (availableICcIds.length === 0) return [];
        return rjMgEsSearch?.usesDhCluster || isDhContractToken(saleToken)
          ? getDhValidDddsForFastchipLines()
          : getEr2ValidDddsForFastchipLines();
      }
      if (availableICcIds.length === 0) return [];
      const estadosICcIds = getEstadosFromAvailableICcIds();
      if (!estadosICcIds.includes(estadoUpper)) {
        return [];
      }
      return getValidDdds(estado, isFastchip, cluster, saleToken);
    },
    [availableICcIds.length, backendAllocationEnabled, getEstadosFromAvailableICcIds, saleToken, teamClusterPreference]
  );

  const [dddOptions, setDddOptions] = useState<Array<{ key: string; text: string; value: string }>>([]);
  const [dddOptionsEdit, setDddOptionsEdit] = useState<Array<{ key: string; text: string; value: string }>>([]);
  const [fastchipDisabled, setFastchipDisabled] = useState(false);
  const [fastchipDisabledReason, setFastchipDisabledReason] = useState('');
  const [iccidsTransientError, setIccidsTransientError] = useState(false);

  useEffect(() => {
    if (clientEstado || isDhContractToken(saleToken)) {
      const validDdds = getValidDddsWithICcIdFallback(clientEstado, fastchip);
      setDddOptions(validDdds.map((ddd) => ({ key: ddd, text: ddd, value: ddd })));
    }
  }, [clientEstado, fastchip, saleToken, availableICcIds, getValidDddsWithICcIdFallback]);

  useEffect(() => {
    if (clientEstado || isDhContractToken(saleToken)) {
      const validDdds = getValidDddsWithICcIdFallback(clientEstado, fastchip);
      setDddOptionsEdit(validDdds.map((ddd) => ({ key: ddd, text: ddd, value: ddd })));
    }
  }, [clientEstado, fastchip, saleToken, availableICcIds, getValidDddsWithICcIdFallback]);

  useEffect(() => {
    if (!clientEstado) {
      setIccidsTransientError(false);
      setFastchipDisabled(false);
      setFastchipDisabledReason('');
      return;
    }

    if (backendAllocationEnabled) {
      // Com alocação backend ativa, não dependemos de pool de ICCIDs no frontend.
      setIccidsTransientError(false);
      setFastchipDisabled(false);
      setFastchipDisabledReason('');
      return;
    }

    // Enquanto carrega, não bloqueia o checkbox de forma otimista.
    if (iccidsLoading) {
      setIccidsTransientError(false);
      setFastchipDisabled(false);
      setFastchipDisabledReason('');
      return;
    }

    if (iccidsError) {
      // Erro transitório: mantém fluxo sem bloquear, mas deixa o estado explícito para nova tentativa manual.
      setIccidsTransientError(true);
      setFastchipDisabled(false);
      setFastchipDisabledReason(
        `Falha temporária ao consultar ICCIDs para ${clientEstado}. Tente novamente em alguns segundos.`
      );
      return;
    }

    setIccidsTransientError(false);

    if (availableICcIdsBase.length === 0) {
      setFastchipDisabled(true);
      setFastchipDisabledReason(
        `Fastchip não está disponível. Não há ICCIDs disponíveis para o ${
          resolveRjMgEsFastchipIccidSearch(clientEstado, teamClusterPreference)
            ? formatRjMgEsFastchipClusterUiLabel(clientEstado)
            : `estado ${clientEstado}`
        }`
      );
      if (fastchip && setFastchip && !isEditingExistingContract) {
        setFastchip(false);
      }
      return;
    }

    setFastchipDisabled(false);
    setFastchipDisabledReason('');
  }, [
    clientEstado,
    fastchip,
    availableICcIdsBase.length,
    iccidsLoading,
    iccidsError,
    setFastchip,
    isEditingExistingContract,
    teamClusterPreference,
    backendAllocationEnabled,
  ]);

  const retryLoadIccids = useCallback(async () => {
    if (backendAllocationEnabled) return;
    await refetchIccids();
  }, [backendAllocationEnabled, refetchIccids]);

  const reservedFastCcids = useMemo(() => {
    const set = new Set<string>();
    for (const linha of linhasComFast ?? []) {
      const ccid = linha.chipCcid?.trim();
      if (ccid) set.add(ccid);
    }
    return set;
  }, [linhasComFast]);

  const fastchipSlotsNeeded = useMemo(() => {
    const linesWithChip = (linhasComFast ?? []).filter((l) => Boolean(l.chipCcid?.trim())).length;
    return linesWithChip + (fastchip ? 1 : 0);
  }, [linhasComFast, fastchip]);

  useEffect(() => {
    if (backendAllocationEnabled || !clientEstado || !fastchip || iccidsLoading || !hasMoreIccids) return;

    const selectableCount = availableICcIds.filter((ic) => !reservedFastCcids.has(ic.ccId)).length;
    if (fastchipSlotsNeeded > selectableCount) {
      void loadMoreIccids();
    }
  }, [
    clientEstado,
    fastchip,
    fastchipSlotsNeeded,
    availableICcIds,
    reservedFastCcids,
    hasMoreIccids,
    iccidsLoading,
    loadMoreIccids,
    backendAllocationEnabled,
  ]);

  return {
    availableICcIds,
    availableICcIdsBase,
    availableICcIdsRaw,
    iccidsLoading,
    iccidsError,
    dddOptions,
    setDddOptions,
    dddOptionsEdit,
    setDddOptionsEdit,
    getValidDdds,
    getEstadosFromAvailableICcIds,
    getValidDddsWithICcIdFallback,
    fastchipDisabled,
    setFastchipDisabled,
    fastchipDisabledReason,
    setFastchipDisabledReason,
    iccidsTransientError,
    retryLoadIccids,
    refetchIccids,
    hasMoreIccids,
    loadMoreIccids,
  };
}
